import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import config from '../config';

  const { apiBaseURL } = config;

  // Helper function to construct API URLs
  export const getApiUrl = (endpoint) => {
    return `${apiBaseURL}${endpoint}`;
  };

  const MarketMovers = ({ onStockSelect }) => {
  const [movers, setMovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    console.log("MarketMovers component mounted"); // Debug log
    
    const fetchMarketMovers = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Use the correct endpoint
        const response = await fetch(`${apiBaseURL}/api/stocks/movers`);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch market movers: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        console.log("Received market movers data:", data);
        
        if (data && data.movers && data.movers.length > 0) {
          // Sort movers by change_percent to separate gainers and losers
          const sortedMovers = [...data.movers].sort((a, b) => 
            parseFloat(b.change_percent) - parseFloat(a.change_percent)
          );
          
          setMovers(sortedMovers);
        } else {
          throw new Error('No market movers data available');
        }
      } catch (err) {
        console.error('Error fetching market movers:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMarketMovers();
  }, []);
  
  const handleStockClick = (symbol) => {
    console.log("Stock clicked:", symbol); // Debug log
    
    // Remove .TO suffix for Canadian stocks if needed
    const cleanSymbol = symbol.replace('.TO', '');
    
    if (onStockSelect) {
      onStockSelect(cleanSymbol);
    } else {
      // If no handler is provided, use the router directly
      navigate(`/${cleanSymbol}`);
    }
  };
  
  if (loading) {
    return (
      <div className="market-movers loading">
        <div className="spinner"></div>
        <p>Loading market data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="market-movers error">
        <h3>Error Loading Market Data</h3>
        <p>{error}</p>
      </div>
    );
  }

  // Split movers into gainers and losers
  const gainers = movers.filter(stock => parseFloat(stock.change_percent) > 0).slice(0, 10);
  const losers = movers.filter(stock => parseFloat(stock.change_percent) < 0)
    .sort((a, b) => parseFloat(a.change_percent) - parseFloat(b.change_percent)).slice(0, 10);

  // Most active sorted by volume
  const mostActive = [...movers].sort((a, b) => b.volume - a.volume).slice(0, 10);

  return (
    <div className="market-movers-container">
      <h2>Today's Market Movers</h2>
      
      <div className="movers-grid">
        {/* Gainers Section */}
        <div className="movers-section gainers">
          <h3>Top Gainers</h3>
          <table className="movers-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Price</th>
                <th>% Change</th>
              </tr>
            </thead>
            <tbody>
              {gainers.length > 0 ? (
                gainers.map((stock, index) => (
                  <tr key={index} className="clickable-row" onClick={() => handleStockClick(stock.symbol)}>
                    <td><span className="stock-symbol">{stock.symbol}</span></td>
                    <td><span className="stock-price">${parseFloat(stock.price).toFixed(2)}</span></td>
                    <td><span className="stock-change-percent positive">+{Math.abs(parseFloat(stock.change_percent)).toFixed(2)}%</span></td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="no-data-message">No data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Losers Section */}
        <div className="movers-section losers">
          <h3>Top Losers</h3>
          <table className="movers-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Price</th>
                <th>% Change</th>
              </tr>
            </thead>
            <tbody>
              {losers.length > 0 ? (
                losers.map((stock, index) => (
                  <tr key={index} className="clickable-row" onClick={() => handleStockClick(stock.symbol)}>
                    <td><span className="stock-symbol">{stock.symbol}</span></td>
                    <td><span className="stock-price">${parseFloat(stock.price).toFixed(2)}</span></td>
                    <td><span className="stock-change-percent negative">-{Math.abs(parseFloat(stock.change_percent)).toFixed(2)}%</span></td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="no-data-message">No data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Most Active Section */}
        <div className="movers-section most-active">
          <h3>Most Active</h3>
          <table className="movers-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Price</th>
                <th>Volume</th>
              </tr>
            </thead>
            <tbody>
              {mostActive.length > 0 ? (
                mostActive.map((stock, index) => (
                  <tr key={index} className="clickable-row" onClick={() => handleStockClick(stock.symbol)}>
                    <td><span className="stock-symbol">{stock.symbol}</span></td>
                    <td><span className="stock-price">${parseFloat(stock.price).toFixed(2)}</span></td>
                    <td><span className="stock-volume">{new Intl.NumberFormat().format(stock.volume)}</span></td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="no-data-message">No data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="market-movers-footer">
        <p>Market data as of {new Date().toLocaleTimeString()}</p>
        <p className="disclaimer">Data may be delayed. For informational purposes only.</p>
      </div>
    </div>
  );
};

export default MarketMovers;